import { MouseEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import closeIcon from 'assets/svg/close-white.svg';
import errorIcon from 'assets/svg/notification-error.svg';
import infoIcon from 'assets/svg/notification-info.svg';
import successIcon from 'assets/svg/notification-success.svg';
import InfoButton from 'components/buttons/InfoButton';
import { IconWrapper } from 'components/containers/Containers';
import { H5 } from 'components/typography/Headers';
import { Text1 } from 'components/typography/Texts';
import { useResponsive } from 'hooks/useResponsive';
import { NotificationData } from 'providers/NotificationProvider';
import { usePopupManager } from 'providers/PopupManagerProvider';

export const AUTO_HIDE_DELAY_MS = 3000;

const Container = styled.div<{
  $backgroundColor: string;
  $isVisible: boolean;
}>`
  width: 100%;
  height: 60px;
  background-color: ${props => props.$backgroundColor};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transform: translateY(${props => (props.$isVisible ? '0' : '-100%')});
  transition: opacity 0.3s ease, transform 0.3s ease;

  ${theme.mq.phone} {
    height: auto;
    min-height: 48px;
  }
`;

const InnerContainer = styled.div<{ $clickable: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
  max-width: ${theme.maxWidthContainer}px;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};

  ${theme.mq.phone} {
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 24px;

  ${theme.mq.phone} {
    justify-content: flex-start;
    padding: 8px 12px;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;

  ${theme.mq.phone} {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 46px;

  ${theme.mq.phone} {
    height: 32px;
    gap: 8px;
    flex-shrink: 0;
  }
`;

const ActionContainer = styled.div`
  margin: auto 24px;
  display: flex;
  height: 100%;
  gap: 20px;

  ${theme.mq.phone} {
    margin: auto 8px auto 0;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
`;

const CloseIcon = styled(IconWrapper)`
  margin-right: 14px;
  cursor: pointer;
  padding: 10px;

  ${theme.mq.phone} {
    margin-right: 4px;
    padding: 6px;
    flex-shrink: 0;
  }
`;

const Title = styled(H5)`
  color: ${theme.color.white};

  ${theme.mq.phone} {
    font-size: 14px;
    line-height: 18px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
`;

const Description = styled(Text1)`
  color: ${theme.color.white};

  ${theme.mq.phone} {
    font-size: 12px;
    line-height: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
`;

interface NotificationProps {
  notification: NotificationData;
  onClose: () => void;
  onClick?: () => void;
}

export const NotificationItem = ({ notification, onClose, onClick }: NotificationProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const { isMobile } = useResponsive();
  const { popups } = usePopupManager();
  const hasOpenPopup = popups.some(popup => !popup.hideRequested);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (!notification.content.autoHide || hasOpenPopup) {
      return;
    }

    const timer = setTimeout(() => {
      handleClose();
    }, AUTO_HIDE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [notification.content.autoHide, hasOpenPopup]);

  const handleCloseClick = (event: MouseEvent<HTMLImageElement>) => {
    event.stopPropagation();
    handleClose();
  };

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>, buttonIndex: 0 | 1) => {
    event.stopPropagation();

    const button = buttonIndex === 0 ? notification.content.firstButton : notification.content.secondButton;
    if (!button) return;

    if (notification.content.inputType === 'Price') {
      // Toast has no price field — open messenger via callback, then close with animation
      notification.content.callback?.();
      handleClose();
      return;
    }

    button.action(notification.content.attributes);
    handleClose();
  };

  const title =
    isMobile && notification.content.mobileTitle ? notification.content.mobileTitle : notification.content.title;
  const description =
    isMobile && notification.content.mobileDescription
      ? notification.content.mobileDescription
      : notification.content.description;

  return (
    <Container
      data-testid={notification.content.type}
      $isVisible={isVisible}
      $backgroundColor={notificationConfig[notification.content.type].backgroundColor}>
      <InnerContainer $clickable={!!onClick} onClick={onClick}>
        <ContentContainer>
          <IconWrapper src={notificationConfig[notification.content.type].icon} />
          <TextContainer>
            {title && <Title>{title}</Title>}
            {description && <Description>{description}</Description>}
          </TextContainer>
        </ContentContainer>
        <ActionContainer>
          {notification.content.type === 'info' && (
            <ButtonContainer>
              {notification.content.firstButton && (
                <InfoButton data-testid={'notification-first-button'} onClick={event => handleButtonClick(event, 0)}>
                  {notification.content.firstButton.label}
                </InfoButton>
              )}
              {notification.content.secondButton && (
                <InfoButton data-testid={'notification-second-button'} onClick={event => handleButtonClick(event, 1)}>
                  {notification.content.secondButton.label}
                </InfoButton>
              )}
            </ButtonContainer>
          )}
          <CloseIcon data-testid={'close-button'} src={closeIcon} onClick={handleCloseClick} />
        </ActionContainer>
      </InnerContainer>
    </Container>
  );
};

interface NotificationConfig {
  [key: string]: {
    backgroundColor: string;
    icon: string;
  };
}

const notificationConfig: NotificationConfig = {
  success: {
    backgroundColor: `${theme.color.notificationGreen}`,
    icon: successIcon,
  },
  info: {
    backgroundColor: `${theme.color.blue}`,
    icon: infoIcon,
  },
  error: {
    backgroundColor: `${theme.color.red}`,
    icon: errorIcon,
  },
};
